import React from 'react';
import { Tooltip } from 'antd';
import {
    CheckCircleFilled,
    CrownFilled,
    ReadOutlined,
    SolutionOutlined,
} from '@ant-design/icons';

const planBadge = (plan) => {
    if (!plan || plan === 'free') return null;
    const isUltimate = plan === 'ultimate';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            background: isUltimate
                ? 'linear-gradient(135deg,#F59E0B,#FCD34D)'
                : 'linear-gradient(135deg,#AD1457,#D81B60)',
            borderRadius: '6px', padding: '3px 8px',
        }}>
            <CrownFilled style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '10px' }} />
            <span style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '0.65rem', fontWeight: 700 }}>
                {plan.toUpperCase()}
            </span>
        </span>
    );
};

const MatchCard = ({ profile }) => {
    const handleCardClick = () => {
        window.open(`/profile?id=${profile.profile_id}`, '_blank');
    };

    const matchPct = profile.match_percentage ?? 0;

    /* gradient for progress bar based on match % */
    const matchColor =
        matchPct >= 80 ? '#10B981' :
        matchPct >= 60 ? '#AD1457' :
        matchPct >= 40 ? '#F59E0B' : '#94A3B8';

    return (
        <div
            className="match-card"
            onClick={handleCardClick}
            style={{
                background: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: '1px solid rgba(173,20,87,0.08)',
                transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                fontFamily: 'Calibri, sans-serif',
                position: 'relative',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 12px 48px rgba(173,20,87,0.2), 0 4px 12px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'rgba(173,20,87,0.2)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(173,20,87,0.08)';
                e.currentTarget.style.borderColor = 'rgba(173,20,87,0.08)';
            }}
        >
            {/* ── Image with overlay ── */}
            <div style={{ position: 'relative', height: '240px', overflow: 'hidden' }}>
                <img
                    alt="profile"
                    src={profile.picture}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.4s ease',
                        display: 'block',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                />

                {/* Bottom gradient overlay */}
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '55%',
                    background: 'linear-gradient(to top, rgba(26,26,46,0.75) 0%, transparent 100%)',
                    pointerEvents: 'none',
                }} />

                {/* Name overlay on image */}
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '12px 14px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{
                            color: 'white', fontWeight: '700',
                            fontSize: '1.05rem', lineHeight: 1.2,
                        }}>
                            {profile.name}
                        </span>
                        {profile.verified && (
                            <Tooltip title="Verified Profile">
                                <CheckCircleFilled style={{ color: '#34D399', fontSize: '16px' }} />
                            </Tooltip>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                        {profile.age && (
                            <span style={{
                                background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                                borderRadius: '6px', padding: '2px 8px',
                                color: 'rgba(255,255,255,0.9)', fontSize: '0.75rem', fontWeight: '500',
                            }}>
                                {profile.age} yrs
                            </span>
                        )}
                        {profile.height && (
                            <span style={{
                                background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                                borderRadius: '6px', padding: '2px 8px',
                                color: 'rgba(255,255,255,0.9)', fontSize: '0.75rem', fontWeight: '500',
                            }}>
                                {profile.height}
                            </span>
                        )}
                    </div>
                </div>

                {/* Plan badge — top right */}
                {profile.membership && profile.membership !== 'free' && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px', boxShadow: '0 3px 10px rgba(0,0,0,0.2)' }}>
                        {planBadge(profile.membership)}
                    </div>
                )}
            </div>

            {/* ── Card Body ── */}
            <div style={{ padding: '16px' }}>
                {/* Key details */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                    {profile.caste && (
                        <span style={{
                            background: '#EDE9FE', color: '#AD1457',
                            borderRadius: '6px', padding: '3px 10px',
                            fontSize: '0.75rem', fontWeight: '600',
                        }}>
                            {profile.caste}
                        </span>
                    )}
                    {profile.denomination && (
                        <span style={{
                            background: '#F0FDF4', color: '#059669',
                            borderRadius: '6px', padding: '3px 10px',
                            fontSize: '0.75rem', fontWeight: '600',
                        }}>
                            {profile.denomination}
                        </span>
                    )}
                </div>

                {/* Education & Occupation */}
                <div style={{ marginBottom: '12px' }}>
                    {profile.education && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                            <ReadOutlined style={{ color: '#94A3B8', fontSize: '0.85rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.85rem' }}>{profile.education}</span>
                        </div>
                    )}
                    {profile.occupation && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                            <SolutionOutlined style={{ color: '#94A3B8', fontSize: '0.85rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.85rem' }}>{profile.occupation}</span>
                        </div>
                    )}
                    {(profile.city || profile.state) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#94A3B8', fontSize: '0.85rem' }}>📍</span>
                            <span style={{ color: '#475569', fontSize: '0.85rem' }}>
                                {[profile.city, profile.state].filter(Boolean).join(', ')}
                            </span>
                        </div>
                    )}
                </div>

                {/* Match percentage */}
                {matchPct > 0 && (
                    <div style={{
                        background: 'rgba(173,20,87,0.04)', borderRadius: '10px', padding: '10px 12px',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: '500' }}>
                                Compatibility
                            </span>
                            <span style={{
                                fontSize: '0.82rem', fontWeight: '700',
                                color: matchColor,
                            }}>
                                {matchPct}%
                            </span>
                        </div>
                        <div style={{
                            height: '6px', background: '#E2E8F0', borderRadius: '9999px', overflow: 'hidden',
                        }}>
                            <div style={{
                                height: '100%', width: `${matchPct}%`,
                                background: `linear-gradient(90deg, ${matchColor}99, ${matchColor})`,
                                borderRadius: '9999px',
                                transition: 'width 0.6s ease',
                            }} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MatchCard;
